import { Injectable } from "@angular/core";
import { AngularFireFunctions } from "@angular/fire/compat/functions";
import { doc, docData, Firestore, getDoc, onSnapshot } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { BehaviorSubject, firstValueFrom, from, Observable, of, throwError } from "rxjs";
import { catchError, map, shareReplay, switchMap } from "rxjs/operators";
import { RequestBodyService } from "src/app/services/request-body.service";
import { RequestPaymentService } from "src/app/services/RequestBodyService/RequestPayment/request-payment.service";

@Injectable()
export class CheckWinningsRepositoryService {
  private claimEventId$ = new BehaviorSubject("");
  public userId: string;
  public surpriseAnimationDocument: string; // to store the current winning document for surprise animation credit gift case
  private creditToConversionRequestCache = new Map<string, Observable<any>>();
  private winningSummaryCache = new Map<string, Data>();

  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private angularFireFunctions: AngularFireFunctions,
    private requestBodyService: RequestBodyService,
    private RequestPaymentService: RequestPaymentService
  ) {
    this.authService.getUid$("CheckWinningsRepositoryService").pipe(
      switchMap((userId) => {
        this.userId = userId || null;
        return of(userId) || of(null);
      })
    );
  }

  isWinning() {
    return this.RequestPaymentService.checkWinningReq().pipe(
      switchMap((reqBody) =>
        this.authService.getV2CloudFunction("apiv2", reqBody)
      ),
      shareReplay(1)
    );
  }


  initiateCheckWinningsRequest(): Observable<any> {
    return this.authService.getUid$("CheckWinningsRepositoryService").pipe(
      switchMap((userId) =>
        this.RequestPaymentService.getWinning().pipe(
          switchMap((reqBody) => {
            if (!reqBody) {
              return of({ error: true });
            }
            return this.authService.getV2CloudFunction("apiv2", reqBody).pipe(
              catchError((err) => of({ error: true, details: err }))
            );
          })
        )
      ),
      catchError(() => of({ error: true })),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  watchCheckWinningsDocument(claimEventId: string): Observable<Data | null> {
    if (!claimEventId) {
      return of(null);
    }

    return this.authService.getUid$("CheckWinningsRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) {
          return throwError(() => new Error("User ID not found"));
        }

        const docRef = doc(this.firestore, `/users/${userId}/check_winnings/${claimEventId}`);

        return new Observable<Data | null>((observer) => {
          const unsubscribe = onSnapshot(
            docRef,
            { includeMetadataChanges: true },
            (snapshot) => {
              observer.next(snapshot?.exists() ? (snapshot.data() as Data) : null);
            },
            (error) => observer.error(error)
          );

          return () => unsubscribe();
        });
      })
    );
  }

  getClaimableSyndicates() {
    return this.authService.getUid$("CheckWinningsRepositoryService").pipe(
      switchMap((userId) =>
        this.RequestPaymentService.getWinning().pipe(
          switchMap((reqBody) =>
            this.authService.getV2CloudFunction("apiv2", reqBody).pipe(
              switchMap((res: any) => {
                if (res?.response?.claim_event_id) {
                  const docRef = doc(
                    this.firestore,
                    `/users/${userId}/check_winnings/${res.response.claim_event_id}`
                  );

                  return docData(docRef).pipe(
                    map((winningData: any) => {
                      if (winningData) {
                        this.cacheWinningSummary(res.response.claim_event_id, winningData);
                      }
                      return {
                        ...res,
                        ...(winningData ?? {}),
                      };
                    }),
                    // Catch Firestore WebChannel transport errors (e.g. on iOS Safari)
                    // which arrive as raw event objects rather than standard Errors,
                    // and would otherwise confuse RxJS's stream handling.
                    catchError((err) => {
                      this.authService.errorHandlerLogs('Firestore lookup failed', err);
                    
                      return of(res);
                    })
                  );
                } else {
                  return of(res);
                }
              }),
              catchError((err) => {
                return of({ error: true, details: err });
              })
            )
          )
        )
      ),
      // Catch any error that escapes the inner chain before shareReplay caches it,
      // preventing replayed error states from crashing new subscribers.
      catchError(() => of({ error: true })),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getCheckWinningSummary$ = this.claimEventId$.pipe(
    switchMap((id) => {
      if (!id) {
        return of(null);
      }
      const cachedSummary = this.winningSummaryCache.get(id);
      if (cachedSummary) {
        return of(cachedSummary);
      }
      return this.authService.getUid$("CheckWinningsRepositoryService").pipe(
        switchMap((userId) => {
          const docRef = doc(this.firestore, `/users/${userId}/check_winnings/${id}`);
          return docData(docRef).pipe(
            map((data) => {
              if (data) {
                this.cacheWinningSummary(id, data);
              }
              return data;
            })
          );
        }));
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  cacheWinningSummary(claimEventId: string, summary: Data): void {
    if (claimEventId && summary) {
      this.winningSummaryCache.set(claimEventId, summary);
    }
  }

  getCreditToConversion(claimEventId: string): Observable<any> {
    if (!claimEventId) {
      return of(null);
    }
    const cachedRequest$ = this.creditToConversionRequestCache.get(claimEventId);
    if (cachedRequest$) {
      return cachedRequest$;
    }
    const request$ = this.RequestPaymentService.getCreditToConversion(claimEventId).pipe(
      switchMap((payload) => {
        if (!payload) {
          return of(null);
        }
        return this.authService.getV2CloudFunction("apiv2", payload);
      }),

      catchError((err) => {
        this.creditToConversionRequestCache.delete(claimEventId);
        return throwError(() => err);
      }),
      shareReplay(1)
    );
    this.creditToConversionRequestCache.set(claimEventId, request$);
    return request$;
  }


  getBreakDown(claimEventId: string): Observable<Data> {
    if (!claimEventId) {
      return of(null);
    }

    return this.authService.getUid$("CheckWinningsRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) {
          return throwError(() => new Error("User ID not found"));
        }

        const docRef = doc(this.firestore, `/users/${userId}/check_winnings/${claimEventId}`);

        return new Observable<Data>((observer) => {
          try {
            const unsubscribe = onSnapshot(
              docRef,
              { includeMetadataChanges: true },
              (snapshot) => {
                try {
                  if (snapshot?.exists()) {
                    observer.next(snapshot?.data() as Data);
                  } else {
                    observer.next(null);
                  }
                } catch (dataError) {
                  observer.error(new Error("Error processing snapshot data: " + dataError));
                }
              },
              (error) => {
                observer.error(error);
              }
            );

            return () => {
              try {
                unsubscribe();
              } catch (unsubscribeError) {
                return unsubscribeError;
              }
            };
          } catch (subscriptionError) {
            observer.error(subscriptionError);
          }
        });
      }));
  }


  getPrizeBreakdownTickets(claimEventId, clickedDate) {
    // const api = this.angularFireFunctions.httpsCallable("api");
    // return api(this.requestBodyService.getCheckedTicketsRequest(claimEventId, clickedDate));
    return this.authService?.getV2CloudFunction(
      "apiv2",
      this.requestBodyService.getCheckedTicketsRequest(claimEventId, clickedDate)
    );
  }

  updateClaimEventId(id) {
    this.claimEventId$.next(id);
  }
  // This method is used to get check winnig gift
  getCheckWinningsGift(documentName: string): Observable<any> {
    if (!documentName) {
      return of(null); // Emit null as an observable if documentName is not provided
    }
    // Create a reference to the Firestore document
    const docRef = doc(this.firestore, `/Global_lookup/check_winnings_gift/list/${documentName}`);
    // Convert the Promise returned by `getDoc()` into an Observable
    return from(getDoc(docRef)).pipe(
      map((res) => res.exists() ? res.data() : null), // Check existence first before extracting data
      catchError(() => {
        return of(null); // Emit null in case of an error
      })
    );
  }
  // Method to fetch promotional card data slide from Firestore
  getPromoCardtDataSlideData(userId: string): Observable<any> {
    return this.authService.getV2CloudFunction("getpromocard", userId).pipe(
      switchMap((data) => {
        if (data && data?.status) {
          const docRef = doc(this.firestore, `users/${userId}/prize_breakdown_slide/slide_1`);
          return from(getDoc(docRef)).pipe(
            map((docSnap) => {
              if (docSnap.exists()) {
                const slideData = docSnap.data();
                return { data: slideData };
              } else {
                return { data: null };
              }
            }),
            catchError((error) => {
              return of({ data: null });
            })
          );
        }
        return of(null); // Return observable of null if no response data is available
      }),
      catchError((error) => {
        return of(null);
      })
    );
  }

  updateVoucherExpiryTime(userId: string, claimEventId: string, voucher: Data): Promise<void> {
  if (!userId || !claimEventId || !voucher) return Promise.resolve();
  if (voucher?.claim_expiry_time) return Promise.resolve();
  const claimTime = voucher?.claim_time_window;
  if (!claimTime) return Promise.resolve();
  const expected = Math.floor((Date.now() + claimTime * 1000) / 1000);
  const existing = voucher?.claim_expiry_time?.seconds;
  if (!existing || Math.abs(existing - expected) > 2) {
    const path_id = "NEJkgQB4o6gEwmazNUlx";
    const doc_data = { voucher: { claim_expiry_time: new Date(expected * 1000) } };
    const dv = { docId: claimEventId };
    const method = "update";
    return firstValueFrom(
      this.authService.getV2CloudFunction("updatefirestoredoc", {
        path_id, doc_data, dv, method,
      })
    ).then(() => {
      // Successfully updated the voucher expiry time
    });
  }
  return Promise.resolve();
}

updateVoucherStatus(userId: string, claimEventId: string, newStatus: string): Promise<void> {
  if (!userId || !claimEventId || !newStatus) return Promise.resolve();
  const updates: Partial<Data> = { voucher: { claim_status: newStatus } };
  const path_id = "NEJkgQB4o6gEwmazNUlx";
  const doc_data = updates;
  const dv = { docId: claimEventId };
  const method = "update";
  return firstValueFrom(
    this.authService.getV2CloudFunction("updatefirestoredoc", {
      path_id, doc_data, dv, method,
    })
  ).then(() => {
    // Successfully updated the voucher status
  });
}

  setVoucherExpiryAndUpdateStatus(
    userId: string,
    claimEventId: string,
    voucher: Data,
    newClaimStatus?: string,
    onSuccess?: () => void
  ): void {
    if (!userId || !claimEventId) return;

    const expiryUpdate = this.updateVoucherExpiryTime(userId, claimEventId, voucher);
    const statusUpdate = newClaimStatus
      ? this.updateVoucherStatus(userId, claimEventId, newClaimStatus)
      : Promise.resolve();

    Promise.all([expiryUpdate, statusUpdate])
      .then(() => onSuccess?.())
      .catch((error) => {
        return error;
      });
  }
}
